import { useState } from 'react';
import VideoModal from './VideoModal';

export default function Solution() {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [activeVideo, setActiveVideo] = useState<string | null>(null);

  const steps = [
    {
      id: 1,
      number: '01',
      title: 'Ver',
      subtitle: 'Mapa en tiempo real',
      description:
        'Plano de planta con estados de color.',
      image:
        '/images/solution-1.png',
      color: 'cyan' as const,
      icon: 'ri-eye-line',
      videoUrl: 'https://www.youtube.com/embed/fTYGDCG7iqY?autoplay=1',
      features: [
        'Visualización en tiempo real del estado de todas las camas',
        'Código de colores intuitivo para identificación rápida',
        'Actualización automática sin necesidad de recargar',
        'Vista de planta completa con todas las habitaciones',
      ],
    },
    {
      id: 2,
      number: '02',
      title: 'Anticipar',
      subtitle: 'Predicción IA',
      description:
        'Probabilidad de alta + horizonte temporal para cada paciente. Criterios visibles y editables.',
      image:
        '/images/solution-2.png',
      color: 'purple' as const,
      icon: 'ri-brain-line',
      videoUrl: 'https://www.youtube.com/embed/lrz__lm_Vxg?autoplay=1',
      features: [
        'Predicción de alta con porcentaje de probabilidad',
        'Estimación de días hasta el alta del paciente',
        'Análisis basado en datos históricos del hospital',
        'Criterios médicos transparentes y editables',
      ],
    },
    {
      id: 3,
      number: '03',
      title: 'Decidir',
      subtitle: 'Asignación inteligente',
      description:
        'IA propone la mejor cama según patología, aislamiento y compatibilidad. Tú validas.',
      image:
        '/images/solution-3.png',
      color: 'amber' as const,
      icon: 'ri-hospital-line',
      videoUrl: 'https://www.youtube.com/embed/wHXW7HeSjXA?autoplay=1',
      features: [
        'Recomendación inteligente de cama óptima',
        'Análisis de compatibilidad de patologías',
        'Verificación de requisitos de aislamiento',
        'Scoring transparente para cada opción',
      ],
    },
    {
      id: 4,
      number: '04',
      title: 'Coordinar',
      subtitle: 'Sin llamadas',
      description:
        'Notificación automática al personal. Confirmación trazable. Estado actualizado en vivo.',
      image:
        '/images/solution-4.png',
      color: 'green' as const,
      icon: 'ri-send-plane-line',
      videoUrl: 'https://www.youtube.com/embed/jfqxb0vznyA?autoplay=1',
      features: [
        'Notificaciones automáticas al equipo pertinente',
        'Confirmación trazable de cada tarea',
        'Actualización automática del estado de la cama',
        'Eliminación completa de llamadas telefónicas',
      ],
    },
  ];

  return (
    <section id="solution-section" className="py-16 sm:py-24 bg-gray-50 relative overflow-hidden">
      {/* Decorative dots */}
      <div className="absolute inset-0 dots-pattern"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-10 sm:mb-16 space-y-3 sm:space-y-4">
          <div className="inline-block glass px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-base sm:text-lg font-bold border border-green-400/30">
            <span className="text-green-600">La Solución</span>
          </div>
          <h2
            className="text-2xl sm:text-4xl lg:text-5xl font-black text-gray-900"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            PreBedAI <span className="gradient-text text-glow">digitaliza</span><br className="hidden sm:block" />
            <span className="sm:hidden"> </span>la coordinación
          </h2>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            Convierte la reunión de gestión de pacientes en un circuito trazable con cuatro pilares
          </p>
        </div>

        {/* Timeline Interactivo Vertical */}
        <div className="relative max-w-5xl mx-auto">
          {/* Steps */}
          <div className="space-y-8 sm:space-y-16">
            {steps.map((step, index) => (
              <div
                key={step.number}
                className={`relative ${index % 2 === 0 ? 'md:pr-1/2' : 'md:pl-1/2 md:text-right'}`}
                onMouseEnter={() => setActiveStep(step.id)}
                onMouseLeave={() => setActiveStep(null)}
              >
                <div
                  className={`flex flex-col md:grid md:grid-cols-2 gap-4 sm:gap-8 items-center ${
                    index % 2 === 1 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  {/* Contenido */}
                  <div className={`${index % 2 === 1 ? 'md:order-2' : ''} space-y-3 sm:space-y-4 w-full`}>
                    <div
                      className={`inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full ${
                        step.color === 'cyan'
                          ? 'bg-cyan-100 text-cyan-700'
                          : step.color === 'purple'
                          ? 'bg-purple-100 text-purple-700'
                          : step.color === 'amber'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                        <i className={`${step.icon} text-lg sm:text-xl`}></i>
                      </div>
                      <span className="font-bold text-sm sm:text-lg">Paso {step.id}</span>
                    </div>

                    <div className={`flex items-center gap-3 sm:gap-4 ${index % 2 === 1 ? 'md:justify-end' : ''}`}>
                      <div
                        className={`w-10 h-10 sm:w-14 sm:h-14 rounded-full flex items-center justify-center shadow-xl flex-shrink-0 transition-all duration-500 ${
                          activeStep === step.id ? 'scale-110' : ''
                        } ${
                          step.color === 'cyan'
                            ? 'bg-cyan-500'
                            : step.color === 'purple'
                            ? 'bg-purple-500'
                            : step.color === 'amber'
                            ? 'bg-amber-500'
                            : 'bg-green-500'
                        }`}
                      >
                        <i className={`${step.icon} text-white text-xl sm:text-2xl`}></i>
                      </div>
                      <h3 className="text-xl sm:text-3xl font-bold text-[#0A1628]">{step.title}</h3>
                    </div>
                    <p className="text-sm sm:text-lg font-semibold text-gray-700">{step.subtitle}</p>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-left">{step.description}</p>

                    {/* Features expandibles - siempre visibles en móvil */}
                    <div
                      className={`space-y-2 transition-all duration-500 ${
                        activeStep === step.id
                          ? 'opacity-100 max-h-96'
                          : 'opacity-100 max-h-96 sm:opacity-70 sm:max-h-0 sm:overflow-hidden md:max-h-96 md:opacity-100'
                      }`}
                    >
                      {step.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2 sm:gap-3">
                          <div
                            className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              step.color === 'cyan'
                                ? 'bg-cyan-500'
                                : step.color === 'purple'
                                ? 'bg-purple-500'
                                : step.color === 'amber'
                                ? 'bg-amber-500'
                                : 'bg-green-500'
                            }`}
                          >
                            <i className="ri-check-line text-white text-xs"></i>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-700">{feature}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Imagen/Video */}
                  <div className={`${index % 2 === 1 ? 'md:order-1' : ''} relative group w-full`}>
                    <div
                      className={`relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl border-2 sm:border-4 transition-all duration-500 ${
                        activeStep === step.id ? 'scale-105 shadow-3xl' : ''
                      } ${
                        step.color === 'cyan'
                          ? 'border-cyan-500'
                          : step.color === 'purple'
                          ? 'border-purple-500'
                          : step.color === 'amber'
                          ? 'border-amber-500'
                          : 'border-green-500'
                      }`}
                    >
                      <img
                        src={step.image}
                        alt={`${step.title} - ${step.subtitle}`}
                        className="w-full h-auto object-contain bg-gray-50"
                      />

                      {/* Botón de video - Siempre visible en móvil, hover en desktop */}
                      <div
                        onClick={() => setActiveVideo(step.videoUrl)}
                        className={`absolute inset-0 bg-gradient-to-t ${
                          step.color === 'cyan'
                            ? 'from-cyan-900/90'
                            : step.color === 'purple'
                            ? 'from-purple-900/90'
                            : step.color === 'amber'
                            ? 'from-amber-900/90'
                            : 'from-green-900/90'
                        } to-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all duration-300 flex items-end justify-center pb-4 sm:pb-8 cursor-pointer`}
                      >
                        <div className="px-4 sm:px-6 py-2 sm:py-3 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer text-xs sm:text-base">
                          <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                            <i className="ri-play-circle-line"></i>
                          </div>
                          <span>Ver video</span>
                        </div>
                      </div>
                    </div>

                    {/* Badge flotante */}
                    <div
                      className={`absolute -top-2 -right-2 sm:-top-4 sm:-right-4 px-2 sm:px-4 py-1 sm:py-2 rounded-full font-bold text-white shadow-xl text-xs sm:text-base ${
                        step.color === 'cyan'
                          ? 'bg-cyan-500'
                          : step.color === 'purple'
                          ? 'bg-purple-500'
                          : step.color === 'amber'
                          ? 'bg-amber-500'
                          : 'bg-green-500'
                      }`}
                    >
                      {step.id}/4
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <VideoModal
          videoUrl={activeVideo}
          onClose={() => setActiveVideo(null)}
        />
      )}
    </section>
  );
}
